// Tiny RAF-based scalar physics, drives reel strip without framer-motion.
// Two modes:
//   spin:  constant velocity scroll with modulo wrap (zero JS state churn).
//   land:  critically-damped spring snapping to a target offset.
//
// Caller passes a writer callback (e.g. el.style.transform = ...) and decides
// when to switch modes. State lives in plain refs, never re-renders Solid.

export interface SpringConfig {
  /** N/m, higher = snappier landing. 320 ≈ short mechanical click. */
  stiffness: number;
  /** Ns/m, higher = less overshoot. */
  damping: number;
  /** kg, usually 1, lower for sharper feel. */
  mass: number;
  /** px tolerance for "settled". */
  restDelta: number;
  /** px/s tolerance for "settled". */
  restSpeed: number;
}

export const DEFAULT_SPRING: SpringConfig = {
  stiffness: 320,
  damping: 26,
  mass: 0.8,
  restDelta: 0.3,
  restSpeed: 0.5,
};

export type WriteFn = (y: number) => void;
type Mode =
  | { kind: "idle" }
  | { kind: "spin"; speed: number }
  | { kind: "land"; target: number; cfg: SpringConfig };

export interface ScalarSpring {
  setSpin(speed: number): void;
  setLand(target: number, cfg?: Partial<SpringConfig>): void;
  setIdle(value?: number): void;
  /** Current position in px (negative scrolls strip upward). */
  position(): number;
  /** Stop RAF + write final state. Idempotent. */
  destroy(): void;
  /** Callback fired whenever we cross a cell boundary while spinning. */
  onTick?: (y: number) => void;
}

/**
 * Wrap strip position so the visible window of `loopPx` repeats forever.
 * Caller's strip must contain ≥3 copies of items so wrap is invisible.
 */
export function createSpring(
  write: WriteFn,
  initial: number,
  loopPx: number,
): ScalarSpring {
  let y = initial;
  let v = 0;
  let mode: Mode = { kind: "idle" };
  let last = performance.now();
  let raf = 0;
  let alive = true;

  const tick = (now: number) => {
    if (!alive) return;
    // Cap dt so a stalled tab doesn't fling the strip miles away.
    const dt = Math.min(0.032, Math.max(0, (now - last) / 1000));
    last = now;

    if (mode.kind === "spin") {
      const prev = y;
      y -= mode.speed * dt;
      // Modulo wrap, keep y in [-loopPx, 0] so transform stays small.
      if (loopPx > 0 && y < -loopPx) y += loopPx;
      // Notify on cell crossing if caller cares (haptic clicks).
      api.onTick?.(y);
      void prev;
      write(y);
    } else if (mode.kind === "land") {
      const { stiffness, damping, mass, restDelta, restSpeed } = mode.cfg;
      // Critically-damped spring integration.
      const dx = y - mode.target;
      const a = (-stiffness * dx - damping * v) / mass;
      v += a * dt;
      y += v * dt;
      if (Math.abs(dx) < restDelta && Math.abs(v) < restSpeed) {
        y = mode.target;
        v = 0;
        mode = { kind: "idle" };
      }
      write(y);
    }

    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame((t) => {
    last = t;
    tick(t);
  });

  const api: ScalarSpring = {
    setSpin(speed) {
      mode = { kind: "spin", speed };
    },
    setLand(target, cfg) {
      mode = {
        kind: "land",
        target,
        cfg: { ...DEFAULT_SPRING, ...cfg },
      };
    },
    setIdle(value) {
      mode = { kind: "idle" };
      if (typeof value === "number") {
        y = value;
        v = 0;
        write(y);
      }
    },
    position: () => y,
    destroy() {
      alive = false;
      if (raf) cancelAnimationFrame(raf);
    },
  };
  return api;
}
